import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, forkJoin, map, of, pipe, switchMap } from 'rxjs';
import { TenantResponse } from '../../api/core/models/api/tenant-response';
import { TenantsService as CoreTenantsService } from '../../api/core/services/tenants.service';
import { ServiceProviderBrandingResponse } from '../../api/service-providers/models/api/service-provider-branding-response';
import { ComponentName } from '../../api/service-providers/models/models/component-name';
import { ServiceProvidersService } from '../../api/service-providers/services';
import { TenantsService } from '../../api/service-providers/services/tenants.service';
import { UserTenant } from '../user/user.types';

export type EnrichedTenant = (UserTenant | TenantResponse) & {
  packagesFeaturesComponents: ComponentName[];
  serviceProviderBranding: ServiceProviderBrandingResponse;
};

type TenantsState = {
  activeTenant: EnrichedTenant | null;
  serviceProviderBrandings: Record<string, ServiceProviderBrandingResponse>;
  tenants: Record<string, EnrichedTenant>;
  isLoading: boolean;
  error: Error | HttpErrorResponse | null;
};

const initialState: TenantsState = {
  activeTenant: null,
  serviceProviderBrandings: {},
  tenants: {},
  isLoading: false,
  error: null,
};

export const TenantsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    const coreTenantsService = inject(CoreTenantsService);
    const serviceProvidersService = inject(ServiceProvidersService);
    const serviceProvidersTenantsService = inject(TenantsService);
    return {
      loadTenant: rxMethod<UserTenant | { id: string }>(
        pipe(
          filter((tenant) => {
            const isTenantActive = store.activeTenant()?.id === tenant.id;
            const isTenantLoaded = !!store.tenants()[tenant.id];
            const isTenantLoading = store.isLoading();

            if (isTenantLoaded && !isTenantActive) {
              patchState(store, { activeTenant: store.tenants()[tenant.id] });
              return false;
            }

            return !isTenantLoading && !isTenantLoaded;
          }),
          switchMap((tenant) => {
            patchState(store, { isLoading: true, error: null });
            const getEnrichedTenant$ = (tenant: UserTenant | TenantResponse) => {
              const loadedBranding = store.serviceProviderBrandings()[tenant.serviceProviderId];
              const branding = !!loadedBranding
                ? of(loadedBranding)
                : serviceProvidersService.getServiceProviderBranding({ id: tenant.serviceProviderId });
              const packages = serviceProvidersTenantsService.listTenantPackages({ id: tenant.id });
              return forkJoin({ packages, branding }).pipe(
                map(({ packages, branding }) => ({ tenant, packages, branding }))
              );
            };
            if (!('serviceProviderId' in tenant)) {
              /**
               * This case happens when a support user is accessing a tenant that the user is not a member of.
               * We then only have access to the tenant ID through the route and need to fetch the tenant from the core API to get the service provider ID.
               * Check handleEditorRouteAccess in editor.guard.ts for the logic behind this.
               */
              return coreTenantsService
                .getTenant({ id: tenant.id })
                .pipe(switchMap((tenant) => getEnrichedTenant$(tenant)));
            }

            return getEnrichedTenant$(tenant);
          }),
          tapResponse({
            next: ({ tenant, packages, branding }) => {
              const enrichedTenant = {
                ...tenant,
                packagesFeaturesComponents: packages.featureComponents,
                serviceProviderBranding: branding,
              };
              patchState(store, {
                activeTenant: enrichedTenant,
                serviceProviderBrandings: {
                  ...store.serviceProviderBrandings(),
                  [tenant.serviceProviderId]: branding,
                },
                tenants: { ...store.tenants(), [tenant.id]: enrichedTenant },
                isLoading: false,
                error: null,
              });
            },
            error: (error: Error | HttpErrorResponse) => {
              patchState(store, { isLoading: false, error });
              console.error('Error loading enriched tenant', error);
              throw error;
            },
          })
        )
      ),
    };
  })
);
